/**
 * Next.js 应用根组件
 * @description 应用全局配置，包括主题、国际化、性能监控等
 * @author 许德峰
 * @date 2025-01-29
 */

/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-require-imports */

// ==================== Console 拦截（必须在所有 import 之前） ====================
// 拦截 findDOMNode 相关警告，避免 React StrictMode 输出已知的无害警告
// 注意：此代码必须在模块顶层执行，确保在 React 组件渲染前生效
if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line no-console
  const originalError = console.error;
  // eslint-disable-next-line no-console
  const originalWarn = console.warn;

  // eslint-disable-next-line no-console
  console.error = function (...args: any[]) {
    // 检查所有参数，不只是第一个（React 可能将警告分成多个参数传递）
    const fullMessage = args
      .map((arg) => {
        if (typeof arg === 'string') return arg;
        if (arg instanceof Error) return arg.message;
        try {
          return String(arg);
        } catch {
          return '';
        }
      })
      .join(' ');

    // 过滤已知的无害警告
    if (
      fullMessage.includes('findDOMNode') ||
      fullMessage.includes('DomWrapper') ||
      fullMessage.includes('ResizeObserver loop')
    ) {
      return;
    }

    originalError.apply(console, args);
  };

  // eslint-disable-next-line no-console
  console.warn = function (...args: any[]) {
    const fullMessage = args
      .map((arg) => {
        if (typeof arg === 'string') return arg;
        if (arg instanceof Error) return arg.message;
        try {
          return String(arg);
        } catch {
          return '';
        }
      })
      .join(' ');

    if (
      fullMessage.includes('findDOMNode') ||
      fullMessage.includes('DomWrapper') ||
      fullMessage.includes('ResizeObserver loop')
    ) {
      return;
    }

    originalWarn.apply(console, args);
  };
}

import '@/utils/polyfills';
import '@/lib/hooks/common/firebaseConfig/init';
import React, { useEffect, useState } from 'react';
import type { NextPage } from 'next';
import type { AppProps } from 'next/app';
import { Router, useRouter } from 'next/router';
import NProgress from 'nprogress';
import '@/styles/mixins.less';
import '@/styles/fonts.css';
import '@/styles/globals.less';
import 'nprogress/nprogress.css';
import '@/layouts/index.less';
import ConfigProvider from '@/context/ConfigProvider';
import UserConfigProvider from '@/context/UserProvider';
import UserProvider from '@/context/UserProvider';
import { AddAccountModalProvider } from '@/context/AddAccountModalProvider';

import withTheme from '@/theme';
import { appWithTranslation } from 'next-i18next';
import { Locale } from 'antd/lib/locale';
import { CurrentLanguage } from '@/shared/language';
import dynamic from 'next/dynamic';

const Login = dynamic(() => import('@/components/Login'), {
  ssr: false,
});

// Antd App 支持 SSR
import { App as AntdApp } from 'antd';

const LoginResult = dynamic(() => import('@/components/LoginResult'), {
  ssr: false,
});

// 配置字体 - Next.js 14 不支持 Geist，使用 CSS 变量方式
// Geist 字体通过 fonts.css 中的 @import 加载

import { AppSettings } from '@/shared/app-common';

// ✅ 优化 NProgress 配置：加快进度条显示速度（从 200ms 降至 50ms）
// 解决所有路由跳转的 INP 问题（200-250ms → 50-100ms）
NProgress.configure({
  minimum: 0.08,
  speed: 50, // 从默认 200ms 降至 50ms，加快进度条出现速度
  trickle: true,
  trickleSpeed: 200,
  showSpinner: false, // 禁用右侧 spinner，减少 DOM 操作
});

Router.events.on('routeChangeStart', NProgress.start);
Router.events.on('routeChangeError', NProgress.done);
Router.events.on('routeChangeComplete', NProgress.done);

require('../styles/ins-theme.less');

const CookieConsentContent = dynamic(() => import('@/components/Common/CookieConsent'), {
  ssr: false,
});

// Web Vitals 性能监控（仅开发/测试环境，生产环境自动禁用）
import WebVitalsProvider from '@/components/Common/WebVitalsProvider';
export type NextPageWithLayout<P = Record<string, unknown>> = NextPage<P> & {
  getLayout?: (page: React.ReactElement) => React.ReactNode;
};

type AppPropsWithLayout<
  P = {
    dehydratedState?: unknown;
  },
> = AppProps<P> & {
  Component: NextPageWithLayout<P>;
};

/**
 * 设置路由事件监听器
 * @description 监听路由变化并控制进度条显示
 */
const setupRouterEvents = () => {
  const handleRouteChangeStart = (url: string) => {
    NProgress.start();
    // 如果是跳转到博客详情页，设置 sessionStorage 标记
    // 这样新组件挂载时就能知道这是路由切换导致的
    if (url.startsWith('/blog/') && url !== '/blog') {
      try {
        sessionStorage.setItem('_article_route_changing', 'true');
        sessionStorage.setItem('_article_previous_route', window.location.pathname);
      } catch {
        // sessionStorage 可能不可用
      }
    }
  };

  const handleRouteChangeComplete = () => {
    NProgress.done();
  };

  const handleRouteChangeError = () => {
    NProgress.done();
    // 路由切换失败时清除标记
    try {
      sessionStorage.removeItem('_article_route_changing');
      sessionStorage.removeItem('_article_previous_route');
    } catch {
      // ignore
    }
  };

  Router.events.on('routeChangeStart', handleRouteChangeStart);
  Router.events.on('routeChangeError', handleRouteChangeError);
  Router.events.on('routeChangeComplete', handleRouteChangeComplete);

  return () => {
    Router.events.off('routeChangeStart', handleRouteChangeStart);
    Router.events.off('routeChangeError', handleRouteChangeError);
    Router.events.off('routeChangeComplete', handleRouteChangeComplete);
  };
};

/**
 * 应用主组件
 * @description 包含全局 Provider、副作用管理、主题配置等
 */
function App({ Component, pageProps }: AppPropsWithLayout) {
  const router = useRouter();
  const [loc, setLoc] = useState<Locale>(() => {
    return CurrentLanguage(router.locale).locale;
  });
  const getLayout = Component.getLayout ?? ((page) => page);

  // ==================== 副作用管理 ====================

  // 1. 设置路由事件监听
  useEffect(() => {
    const cleanup = setupRouterEvents();
    return cleanup;
  }, []);

  // 2. 设置国际化语言环境
  useEffect(() => {
    setLoc(CurrentLanguage(router.locale).locale);
  }, [router.locale]);

  // 3. 保存 URL 来源参数到本地存储
  useEffect(() => {
    if (router.isReady && !localStorage.getItem('source') && router.query.source) {
      localStorage.setItem('source', router.query.source as string);
    }
  }, [router.isReady, router.query.source]);

  // 4. 加载 Clarity 分析脚本
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const loadClarityScript = () => {
      const timer = setTimeout(() => {
        const script = document.createElement('script');
        script.innerHTML = `(function(c,l,a,r,i,t,y){
          c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
          t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
          y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
        })(window, document, "clarity", "script", "${AppSettings.clarityId}");`;
        document.body.appendChild(script);

        // Rewardful 联盟归因：queue stub 必须先于 rw.js 注入，否则早于脚本加载的调用会丢失。
        // 下单时由 plan / ThreeFree 读取 window.Rewardful.referral 作为 referral_id 上报。
        // data-rewardful 必须是 Rewardful 后台的 API key（非 Company token），
        // 且与联盟伙伴注册所在的账号一致（erasa-dmca.getrewardful.com），
        // 否则归属不会记录且不报错。1.15.0 已从旧账号的 e19b2f 换为 aabeb5。
        const rewardfulStub = document.createElement('script');
        rewardfulStub.innerHTML = `(function(w,r){w._rwq=r;w[r]=w[r]||function(){(w[r].q=w[r].q||[]).push(arguments)}})(window,'rewardful');`;
        document.body.appendChild(rewardfulStub);
        const rewardfulScript = document.createElement('script');
        rewardfulScript.src = 'https://r.wdfl.co/rw.js';
        rewardfulScript.setAttribute('data-rewardful', 'aabeb5');
        rewardfulScript.setAttribute('async', 'true');
        document.body.appendChild(rewardfulScript);
        clearTimeout(timer);
      }, 0);
    };

    if (document.readyState === 'complete') {
      loadClarityScript();
    } else {
      window.addEventListener('load', loadClarityScript);
      return () => window.removeEventListener('load', loadClarityScript);
    }
  }, []);

  // 4. 设置视口高度变量（兼容老 iOS / 内嵌 WebView / 国产浏览器）
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const setVh = () => {
      const height = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      document.documentElement.style.setProperty('--vh', `${height * 0.01}px`);
    };

    setVh();

    window.visualViewport?.addEventListener('resize', setVh);
    window.addEventListener('resize', setVh);

    return () => {
      window.visualViewport?.removeEventListener('resize', setVh);
      window.removeEventListener('resize', setVh);
    };
  }, []);

  return withTheme(
    <WebVitalsProvider>
      <div className="antialiased">
        <AntdApp>
          <ConfigProvider>
            <UserProvider>
              <UserConfigProvider>
                <AddAccountModalProvider>
                  {getLayout(<Component {...pageProps} />)}
                  <Login />
                  <LoginResult />
                  <CookieConsentContent />
                </AddAccountModalProvider>
              </UserConfigProvider>
            </UserProvider>
          </ConfigProvider>
        </AntdApp>
      </div>
    </WebVitalsProvider>,
    loc,
  );
}

export default appWithTranslation(App);
